/**
 * Keyboard Shortcuts Help Modal
 * Shows all available keyboard shortcuts
 * Triggered by pressing "?"
 * Part of Phase 6E - UX Enhancements
 */

import React, { useEffect, useState } from 'react';
import { Modal, Typography, Row, Col, Tag, Divider, Space } from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  DashboardOutlined,
  SettingOutlined,
  QuestionOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface Shortcut {
  key: string;
  description: string;
  category: string;
}

const shortcuts: Shortcut[] = [
  // Global
  { key: 'Ctrl+K / Cmd+K', description: 'Open global search', category: 'Global' },
  { key: '?', description: 'Show this help dialog', category: 'Global' },
  { key: 'Esc', description: 'Close modals/dialogs', category: 'Global' },
  { key: 'Alt+N', description: 'New document (contextual)', category: 'Global' },
  { key: 'Ctrl+S / Cmd+S', description: 'Save current form', category: 'Global' },
  { key: 'Ctrl+Shift+A', description: 'Toggle ERP AI assistant panel', category: 'Global' },

  // Navigation
  { key: 'G then D', description: 'Go to Dashboard', category: 'Navigation' },
  { key: 'G then S', description: 'Go to Sales', category: 'Navigation' },
  { key: 'G then M', description: 'Go to Manufacturing', category: 'Navigation' },
  { key: 'G then I', description: 'Go to Inventory', category: 'Navigation' },
  { key: 'G then F', description: 'Go to Finance', category: 'Navigation' },
  { key: 'G then Q', description: 'Go to Quality', category: 'Navigation' },
  { key: 'G then P', description: 'Go to Procurement', category: 'Navigation' },
  { key: 'Alt+←', description: 'Go back', category: 'Navigation' },
  { key: 'Alt+→', description: 'Go forward', category: 'Navigation' },
  
  // Search & Filter
  { key: '/', description: 'Focus search (in lists)', category: 'Search' },
  { key: 'Ctrl+F / Cmd+F', description: 'Find in page', category: 'Search' },
  
  // List Navigation
  { key: '↑ ↓', description: 'Navigate list items', category: 'Lists' },
  { key: 'Enter', description: 'Open selected item', category: 'Lists' },
  { key: 'Ctrl+A / Cmd+A', description: 'Select all', category: 'Lists' },
  { key: 'Shift+Click', description: 'Select range', category: 'Lists' },
  
  // Forms
  { key: 'Tab', description: 'Next field', category: 'Forms' },
  { key: 'Shift+Tab', description: 'Previous field', category: 'Forms' },
  { key: 'Ctrl+Enter / Cmd+Enter', description: 'Submit form', category: 'Forms' },
  
  // Actions
  { key: 'E', description: 'Edit (when item selected)', category: 'Actions' },
  { key: 'Delete', description: 'Delete (when item selected)', category: 'Actions' },
  { key: 'Ctrl+P / Cmd+P', description: 'Print', category: 'Actions' },
  { key: 'Ctrl+D / Cmd+D', description: 'Duplicate', category: 'Actions' },
];

export const KeyboardShortcutsHelp: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Open help on "?"
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Don't trigger if user is typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return;
        }
        
        e.preventDefault();
        setVisible(true);
      }
      
      // Close on Escape
      if (e.key === 'Escape' && visible) {
        setVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible]);

  const categories = [...new Set(shortcuts.map(s => s.category))];

  return (
    <Modal
      title={
        <Space>
          <QuestionOutlined style={{ color: '#145A54' }} />
          <Text strong>Keyboard Shortcuts</Text>
        </Space>
      }
      open={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={800}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Text type="secondary">
          Press <Tag>?</Tag> anytime to open this help. Press <Tag>Esc</Tag> to close.
        </Text>

        {categories.map((category) => {
          const categoryShortcuts = shortcuts.filter(s => s.category === category);
          
          return (
            <div key={category}>
              <Title level={5}>{category}</Title>
              <Row gutter={[16, 8]}>
                {categoryShortcuts.map((shortcut, index) => (
                  <Col span={24} key={index}>
                    <Row justify="space-between" align="middle">
                      <Col span={16}>
                        <Text>{shortcut.description}</Text>
                      </Col>
                      <Col span={8} style={{ textAlign: 'right' }}>
                        {shortcut.key.split(' ').map((key, i) => (
                          <Tag key={i} style={{ marginLeft: 4 }}>
                            {key}
                          </Tag>
                        ))}
                      </Col>
                    </Row>
                  </Col>
                ))}
              </Row>
              {category !== categories[categories.length - 1] && <Divider />}
            </div>
          );
        })}

        <div style={{ background: '#f0f5ff', padding: 12, borderRadius: 4, marginTop: 16 }}>
          <Text strong>💡 Pro Tip:</Text>
          <br />
          <Text type="secondary">
            Most shortcuts can be customized in Settings → User Preferences → Keyboard Shortcuts
          </Text>
        </div>
      </Space>
    </Modal>
  );
};

export default KeyboardShortcutsHelp;
